import { useState, useEffect } from "react";
import "@fontsource/montserrat";
import { Paper, Group, Button, Box } from "@mantine/core";
import { PX_PER_MM } from "../components/builder/constants";
import { useCanvasEditor } from "../components/builder/useCanvasEditor";
import { useBlobUrlCleanup } from "../components/builder/useBlobUrlCleanup";
import { toMm } from "../components/builder/badge/badgeConstants";
import BadgePalette from "../components/builder/badge/BadgePalette";
import BadgeElementEditor from "../components/builder/badge/BadgeElementEditor";
import BadgeCanvas from "../components/builder/badge/BadgeCanvas";
import { rasterizeStockToPng } from "../utils/rasterizeStock";

const ACCEPTED_STOCK_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/svg+xml",
];

export default function BadgeBuilder({
  initialValue,
  onSubmit,
  onCancel,
  saving = false,
}) {
  const [size, setSize] = useState(
    initialValue?.size || { width: 4, height: 3, unit: "in" }
  );
  const [bgImage, setBgImage] = useState(initialValue?.bgImage || null);
  const [bgFile, setBgFile] = useState(null);
  const [backgroundFit, setBackgroundFit] = useState(
    initialValue?.backgroundTransform?.fit || "fit"
  );
  const [elements, setElements] = useState(initialValue?.elements || []);
  const [selectedId, setSelectedId] = useState(null);
  const [previewLayer, setPreviewLayer] = useState("preview");
  const [bgError, setBgError] = useState(null);

  const nativeWidth = toMm(size.width, size.unit) * PX_PER_MM;
  const nativeHeight = toMm(size.height, size.unit) * PX_PER_MM;

  const {
    containerRef,
    paneRef,
    scale,
    draggingId,
    handlePointerDown,
    handleResizePointerDown,
    handlePointerMove,
    handlePointerUp,
  } = useCanvasEditor({
    items: elements,
    setItems: setElements,
    nativeWidth,
    nativeHeight,
  });

  useBlobUrlCleanup(bgImage);

  useEffect(() => {
    if (!selectedId) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelectedId(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId]);

  const handleBgUpload = async (file) => {
    setBgError(null);
    if (!file) {
      setBgImage(null);
      setBgFile(null);
      return;
    }
    if (!ACCEPTED_STOCK_TYPES.includes(file.type)) {
      setBgError("Only PDF, PNG, JPG, and SVG files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setBgError("File size must not exceed 5MB.");
      return;
    }
    try {
      const png = await rasterizeStockToPng(file);
      setBgImage(URL.createObjectURL(png));
      setBgFile(png);
    } catch {
      setBgError("Could not process the file. Try a different one.");
    }
  };

  const addElement = (type, fieldKey) => {
    const newId = Date.now().toString();
    const base = {
      id: newId,
      type,
      layer: "print",
      xPct: 10,
      yPct: 10,
      widthPct: 30,
      heightPct: type === "qr" ? 25 : type === "rectangle" ? 20 : 8,
    };

    if (type === "field") {
      base.fieldKey = fieldKey;
      base.fontSize = 16;
      base.color = "#000000";
      base.align = "left";
    } else if (type === "text") {
      base.text = "Text";
      base.fontSize = 16;
      base.color = "#000000";
      base.align = "left";
    } else if (type === "rectangle") {
      base.fill = "#e9ecef";
    }

    setElements((prev) => [...prev, base]);
    setSelectedId(newId);
  };

  const removeElement = (id, e) => {
    if (e) e.stopPropagation();
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const updateElement = (id, key, value) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, [key]: value } : el))
    );
  };

  const onElementPointerDown = (e, el) => {
    if (e.target.closest(".resize-handle")) return;
    setSelectedId(el.id);
    handlePointerDown(e, el.id, el.xPct, el.yPct);
  };

  const onResizePointerDown = (e, id, widthPct, heightPct) => {
    setSelectedId(id);
    handleResizePointerDown(e, id, widthPct, heightPct);
  };

  const selectedItem = elements.find((el) => el.id === selectedId);

  // Fields may be transiently empty while editing; apply fallbacks on save so
  // an emptied width/height/fontSize never persists as "".
  const handleSave = () => {
    onSubmit({
      size: {
        width: size.width || 1,
        height: size.height || 1,
        unit: size.unit,
      },
      bgImage,
      bgFile,
      backgroundTransform: { fit: backgroundFit },
      elements: elements.map((el) =>
        el.fontSize !== undefined ? { ...el, fontSize: el.fontSize || 12 } : el
      ),
    });
  };

  return (
    <Group
      p={0}
      m={0}
      style={{
        height: "calc(100vh - 100px - var(--mantine-spacing-md) * 2)",
        overflow: "hidden",
      }}
      align="stretch"
      wrap="nowrap"
    >
      <Paper
        w={350}
        p="md"
        shadow="sm"
        radius={0}
        style={{
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          overflowY: "auto",
        }}
      >
        {!selectedItem ? (
          <BadgePalette
            size={size}
            setSize={setSize}
            bgFile={bgFile}
            bgError={bgError}
            onBgUpload={handleBgUpload}
            hasStock={!!bgImage}
            backgroundFit={backgroundFit}
            setBackgroundFit={setBackgroundFit}
            elements={elements}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            addElement={addElement}
            removeElement={removeElement}
          />
        ) : (
          <BadgeElementEditor
            element={selectedItem}
            size={size}
            updateElement={updateElement}
            removeElement={removeElement}
            onBack={() => setSelectedId(null)}
          />
        )}

        {!selectedItem && (
          <Box mt="auto">
            <Group grow>
              {onCancel && (
                <Button variant="light" color="gray" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              {onSubmit && (
                <Button color="teal" loading={saving} onClick={handleSave}>
                  Save Badge
                </Button>
              )}
            </Group>
          </Box>
        )}
      </Paper>

      <BadgeCanvas
        containerRef={containerRef}
        paneRef={paneRef}
        scale={scale}
        nativeWidth={nativeWidth}
        nativeHeight={nativeHeight}
        bgImage={bgImage}
        backgroundFit={backgroundFit}
        elements={elements}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        previewLayer={previewLayer}
        setPreviewLayer={setPreviewLayer}
        draggingId={draggingId}
        onElementPointerDown={onElementPointerDown}
        onResizePointerDown={onResizePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
    </Group>
  );
}
